'use strict';

const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
const CURRENT_ADMIN_LOCAL_STORAGE_KEY = 'App-Admin';
const PWD_REGEX_KEY = 'Password-Regex';

export default angular
  .module('main.profile.dataProvider', [])
  .factory('ProfileDataProvider', function(
    $filter,
    utils,
    UsersService,
    AuthService,
    BrandsService,
    Resource,
    ResourceGroup,
    I18nService,
    $state,
    $q
  ) {
    const $t = $filter('translate');
    const $translateOrDefault = $filter('translateOrDefault');

    class ProfileDataProvider {

      // #region Public fields

      // #region Resources

      /**
       * Current organization
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(
        () => {
          // TODO move get/set current org stuff to the OrganizationService
          return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current user (admin currently logged in)
       * @type {Resource}
       */
      userInfoResource = new Resource(
        () => {
          return UsersService.getCurrentUserInfo(true);
        }
      );

      /**
       * Current User ID
       * @type {Resource}
       */
      userIdResource = new Resource(
        () => {
          return this.userInfoResource.value?.user?.id;
        },
        [
          this.userInfoResource
        ]
      );

      /**
       * Current User name
       * @type {Resource}
       */
      userNameResource = new Resource(
        () => {
          const user = this.userInfoResource.value?.user;
          return [
              (user?.firstName || '').trim(),
              (user?.lastName || '').trim()
            ]
              .join(' ')
              .trim()
            || $t('common.unknownUser');
        },
        [
          this.userInfoResource
        ]
      );

      /**
       * Current User role name
       * @type {Resource}
       */
      userRoleResource = new Resource(
        () => {
          const user = this.userInfoResource.value?.user;
          return user?.role?.name;
        },
        [
          this.userInfoResource
        ]
      );

      /**
       * List of current user permissions as string
       * @type {Resource}
       */
      userPermissionsStrResource = new Resource(
        () => {
          const user = this.userInfoResource.value?.user;
          const permissions = user?.permission || [];
          let str = permissions
            .map(p => $translateOrDefault('orgs.permissions.' + p, $t('orgs.permissions.unknown')))
            .filter(e => !!e)
            .join(', ');
          if (str) {
            str = str + '.';
          }
          return str;
        },
        [
          this.userInfoResource
        ]
      );

      /**
       * List of available user languages
       * @type {Resource}
       */
      availableLanguagesResource = new Resource(
        () => {
          return I18nService.getLocalesList() || [];
        }
      );

      /**
       * Password Regexp
       * @type {Resource}
       */
      passwordRegexpResource = new Resource(
        () => {
          let regex = utils.getStorageItem(PWD_REGEX_KEY);
          if (regex) {
            return regex;
          }

          return AuthService.getPasswordRegex()
            .then((data) => {
              utils.setStorageItem(PWD_REGEX_KEY, data);
              return data;
            });
        }
      );

      /**
       * Passcode Timeout
       * @type {Resource}
       */
      passcodeTimeoutResource = new Resource(
        () => {
          return AuthService.getPasscodeResendTimeout();
        }
      );

      /**
       * List of TOTP factors (linked devices)
       * @type {Resource}
       */
      totpListResource = new Resource(
        () => {
          return AuthService.getTotpFactors()
            .then(data => data?.factors || []);
        }
      );

      /**
       * Current application branded name
       * @type {Resource}
       */
      appNameResource = new Resource(
        () => {
          return BrandsService.getBrandConfig()?.pageTitle;
        }
      );

      /**
       * Current user preferred System of measurement
       * @type {Resource}
       */
      userSystemOfMeasurementResource = new Resource(
        () => {
          return UsersService.getSystemOfMeasurement();
        }
      );

      // do we need this in ui?
      // isUnlimitedAdmin = PermissionsService.isUnlimitedAdmin();
      // isPaidAdmin = PermissionsService.isPaidAdmin();
      // isServiceAdmin = PermissionsService.isServiceAdmin();

      /**
       * Resources that we need for every tab
       * @type {ResourceGroup}
       */
      mainResourceGroup = new ResourceGroup([
        this.currentOrganizationResource,
        this.userInfoResource,
        this.userIdResource,
        this.userNameResource,
        this.userRoleResource,
      ]);

      // #endregion

      /**
       * Current (active) tab
       * @type {'dashboard'|'changePassword'|'totp'|'addTotp'}
       */
      get activeTab() {
        return this.#_activeTab;
      }

      /**
       * Previous tab
       * @type {'dashboard'|'changePassword'|'totp'|'addTotp'}
       */
      get previousTab() {
        return this.#_previousTab;
      }

      set activeTab(val) {
        this.#_visitedTabs[val] = true;
        if (this.#_activeTab !== val) {
          this.#_previousTab = this.#_activeTab;
        }
        this.#_activeTab = val;
      }

      /**
       * Hash of visited tabs
       */
      get visitedTabs() {
        return this.#_visitedTabs;
      }

      // #endregion

      // #region Private fields

      #_activeTab;

      #_previousTab;

      #_visitedTabs = {};

      // #endregion

      constructor() {
      }

      // #region Public methods

      /**
       * Universal handle function for Cancel or Back button
       */
      goBack() {
        if ($state.params && $state.params.from) {
          switch ($state.params.from) {
            case 'user':
              $state.go('main.users.edit');
              return;
            case 'device':
              $state.go('main.devices.edit');
              return;
            case 'devices':
              $state.go('main.devices.list');
              return;
            case 'notification':
              $state.go('main.notifications.info');
              return;
            case 'notifications':
              $state.go('main.notifications.list');
              return;
          }
        }
        $state.go('main.locations.list');
      }

      /**
       * Reload current state.
       */
      reloadState() {
        $state.reload();
      }

      /**
       * Destroy current Data Provider.
       * Obviously, you can not use this object after destruction.
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }

        // Clear cache here
      }

      /**
       * Save user
       * @param user
       * @returns {Promise}
       */
      saveUser(user) {
        const originalUser = this.userInfoResource.value?.user;
        if (!user || !originalUser) {
          return $q.reject();
        }

        const changeUserModel = {
          email: user.email?.email,
          phone: user.phone,
          phoneType: 1,
          firstName: user.firstName,
          lastName: user.lastName,
          // username: user.userName, // NOTE: userName is readonly for now. Lower/camel case mismatch in 'userName' field name is not a bug here.
          // altUsername: user.altUsername // NOTE: altUsername is readonly for now
          // language: user.language // NOTE: locale is saved right after change
        };

        return UsersService.updateUserInfo(changeUserModel)
          .then(() => this.userInfoResource.init(true));
      }

      /**
       * Change current user (and current system) locale
       * @param {string} lngName
       * @returns {Promise<{code: string, flag: string}>}
       */
      changeUserLanguage(lngName) {
        const originalUser = this.userInfoResource.value?.user;

        if (!lngName || !originalUser) {
          return $q.reject();
        }

        const origLocale = I18nService.getLocale();
        I18nService.setLocale(lngName);
        const newLocale = I18nService.getLocale();

        if (originalUser.language === newLocale.code) {
          return $q.resolve(newLocale);
        }

        return UsersService.updateUserInfo({
          language: newLocale.code
        })
          .then(() => {
            const userInfo = this.userInfoResource.value?.user;
            if (!!userInfo?.user) {
              userInfo.user.language = newLocale.code;
              utils.setStorageItem(CURRENT_ADMIN_LOCAL_STORAGE_KEY, userInfo);
              this.userInfoResource.forceReInitDependentsRecursively();
            }
            return newLocale;
          }, (err) => {
            I18nService.setLocale(origLocale.code);
            return $q.reject(err);
          });
      }

      setNewPassword(newPwd, oldPwd) {
        return AuthService.setNewPassword(
          newPwd,
          oldPwd,
          void 0,
          {
            strongPassword: true,
            keepKeyVersion: true, // preserve current API key
          });
      }

      confirmPasscode(newPwd, oldPwd, passcode) {
        return AuthService.setNewPassword(
          newPwd,
          oldPwd,
          passcode.toUpperCase(),
          {
            keepKeyVersion: true,
          }
        );
      }

      setNewPasswordByTotp(newPwd, oldPwd) {
        return AuthService.setNewPassword(
          newPwd,
          oldPwd,
          void 0,
          {
            strongPassword: true,
            keepKeyVersion: true, // preserve current API key
            totp: true
          });
      }

      confirmNewPasswordByTotp(newPwd, oldPwd, totp) {
        return AuthService.setNewPassword(
          newPwd,
          oldPwd,
          totp,
          {
            keepKeyVersion: true,
            totp: true,
          }
        );
      }

      deleteTotpFactor(factor) {
        return AuthService.deleteTotpFactor(factor.name)
          .then(() => {
            this.totpListResource.value = (this.totpListResource.value || [])
              .filter(f => f.name !== factor.name);
            this.totpListResource.forceReInitDependentsRecursively();
          });
      }

      createTotpFactor(name, issuer) {
        return AuthService.createTotpSecret({ name: name, issuer: issuer });
      }

      confirmTotpFactor(name, code) {
        return AuthService.confirmTotpSecret(name, code);
      }

      deleteAdminAccount() {
        return UsersService.deleteUser();
      }

      /**
       * Save current user preferred System of measurement
       * @param {'metric' | 'us'} systemOfMeasurement
       */
      updateSystemOfMeasurement(systemOfMeasurement) {
        return UsersService.updateSystemOfMeasurement(systemOfMeasurement)
          .then(() => this.userSystemOfMeasurementResource.value = systemOfMeasurement);
      }

      // #endregion

    }

    return ProfileDataProvider;
  })
  .name;



